/**
 * AddSIModal Component
 * Form for creating and editing Supplemental Instructions.
 * Follows AddRFIModal pattern.
 */
import { useState, useEffect } from 'react';
import { SI, SICreate } from '@/types/supplemental_instructions';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCreateSI, useUpdateSI } from '@/hooks';

interface AddSIModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  editingSI?: SI | null;
}

const EMPTY_FORM: Partial<SICreate> = {
  title: '',
  description: '',
  issued_date: '',
  issued_by: '',
  drawing_references: '',
  spec_references: '',
};

export function AddSIModal({ isOpen, onClose, projectId, editingSI }: AddSIModalProps) {
  const [formData, setFormData] = useState<Partial<SICreate>>(EMPTY_FORM);

  const createSI = useCreateSI();
  const updateSI = useUpdateSI();
  const isSaving = createSI.isPending || updateSI.isPending;

  useEffect(() => {
    if (editingSI) {
      setFormData({
        title: editingSI.title ?? '',
        description: editingSI.description ?? '',
        issued_date: editingSI.issued_date ?? '',
        issued_by: editingSI.issued_by ?? '',
        drawing_references: editingSI.drawing_references ?? '',
        spec_references: editingSI.spec_references ?? '',
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [editingSI, isOpen]);

  const handleChange = (field: keyof SICreate, value: string | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.title?.trim()) return;

    const payload: SICreate = {
      title: formData.title!.trim(),
      description: formData.description || null,
      issued_date: formData.issued_date || null,
      issued_by: formData.issued_by || null,
      drawing_references: formData.drawing_references || null,
      spec_references: formData.spec_references || null,
    };

    try {
      if (editingSI) {
        await updateSI.mutateAsync({ id: editingSI.id, data: payload });
      } else {
        await createSI.mutateAsync({ projectId, si: payload });
      }
      onClose();
    } catch {
      // Error is handled in the hook via toast
    }
  };

  const handleClose = () => {
    setFormData(EMPTY_FORM);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editingSI ? 'Edit Supplemental Instruction' : 'New Supplemental Instruction'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="si-title">Title <span className="text-destructive">*</span></Label>
            <Input
              id="si-title"
              value={formData.title ?? ''}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Brief description of the instruction"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="si-description">Instruction Details</Label>
            <Textarea
              id="si-description"
              value={formData.description ?? ''}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Full text of the supplemental instruction..."
              rows={5}
            />
          </div>

          {/* Two-col: issued_by + issued_date */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="si-issued-by">Issued By</Label>
              <Input
                id="si-issued-by"
                value={formData.issued_by ?? ''}
                onChange={(e) => handleChange('issued_by', e.target.value)}
                placeholder="Architect name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="si-issued-date">Date Issued</Label>
              <Input
                id="si-issued-date"
                type="date"
                value={formData.issued_date ?? ''}
                onChange={(e) => handleChange('issued_date', e.target.value)}
              />
            </div>
          </div>

          {/* Two-col: drawing_references + spec_references */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="si-drawing-ref">Drawing Reference</Label>
              <Input
                id="si-drawing-ref"
                value={formData.drawing_references ?? ''}
                onChange={(e) => handleChange('drawing_references', e.target.value)}
                placeholder="e.g. A1.01, S2.03"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="si-spec-ref">Spec Reference</Label>
              <Input
                id="si-spec-ref"
                value={formData.spec_references ?? ''}
                onChange={(e) => handleChange('spec_references', e.target.value)}
                placeholder="e.g. 03 30 00"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSaving || !formData.title?.trim()}
            >
              {isSaving ? 'Saving...' : editingSI ? 'Save Changes' : 'Create SI'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
